import axios from 'axios';
import * as XLSX from 'xlsx';
import api from './api';
import { INVENTORY_IMPORT_HEADERS } from '../utils/inventoryImportTemplate';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ImportAction = 'CREATE' | 'UPDATE' | 'ERROR';
export type CommitStatus = 'SUCCESS' | 'ERROR' | 'SKIPPED';

export interface InventoryImportPreviewRow {
  rowNumber: number;
  action: ImportAction;
  warnings: string[];
  errors: string[];
  resolved: {
    wineId: string | null;
    inventoryDisplayName: string | null;
    supplierId: string | null;
    supplierAction: 'LINK' | 'AUTO_CREATE' | null;
    locationId: string | null;
    willUseDefaultLocation: boolean;
    wineListId: string | null;
    existingItemId: string | null;
  };
  data: {
    itemType: 'WINE' | 'GENERIC' | null;
    itemName: string | null;
    sizeValue: number | null;
    sizeUnit: string | null;
    supplierName: string | null;
    wineListName: string | null;
  };
}

export interface InventoryImportPreviewResponse {
  totalRows: number;
  summary: { create: number; update: number; error: number };
  rows: InventoryImportPreviewRow[];
}

export interface InventoryImportCommitRow {
  rowNumber: number;
  action: ImportAction;
  status: CommitStatus;
  errors: string[];
  warnings: string[];
  result: {
    inventoryItemId: string | null;
    supplierCreated: boolean;
    supplierLinked: boolean;
    wineListLinked: boolean;
  };
}

export interface InventoryImportCommitResponse {
  totalRows: number;
  summary: { created: number; updated: number; errored: number };
  rows: InventoryImportCommitRow[];
}

// ── Export row type (matches backend InventoryExportRow) ──────────────────────

export interface InventoryExportRow {
  itemType: 'WINE' | 'GENERIC';
  itemName: string;
  sizeValue: number | null;
  sizeUnit: string | null;
  active: boolean;
  binLocation: string | null;
  parLevel: number | null;
  reorderPoint: number | null;
  supplierName: string | null;
  supplierItemCode: string | null;
  cost: number | null;
  wineListName: string | null;
  sellingPrice: number | null;
  lotTracked: boolean;
}

// ── Error extraction ──────────────────────────────────────────────────────────

const IMPORT_ERROR_CODES: Record<string, string> = {
  IMPORT_NO_FILE: 'No file uploaded.',
  IMPORT_INVALID_FILE_TYPE: 'Unsupported file type — upload a .xlsx or .csv file.',
  IMPORT_PARSE_ERROR: 'Could not parse the file. Check it is a valid Excel file.',
  IMPORT_EMPTY_FILE: 'The uploaded file contains no data rows.',
  IMPORT_ROW_LIMIT_EXCEEDED: 'File exceeds the 500-row limit — split and import in batches.',
  IMPORT_MISSING_HEADERS: 'File is missing required column headers.',
};

export function extractInventoryImportError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errObj?.['code'];
    if (typeof code === 'string' && IMPORT_ERROR_CODES[code]) {
      const missing = errObj?.['missing'];
      if (code === 'IMPORT_MISSING_HEADERS' && Array.isArray(missing)) {
        return `Missing columns: ${(missing as string[]).join(', ')}`;
      }
      return IMPORT_ERROR_CODES[code];
    }
    const msg = errObj?.['message'] ?? data?.['message'];
    if (typeof msg === 'string' && msg) return msg;
    if (err.response?.status === 413) return 'File is too large (max 10 MB).';
    if (err.response?.status === 403) return 'You do not have permission to import.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

// ── Existing-items export ─────────────────────────────────────────────────────

export function downloadExistingItemsAsImport(rows: InventoryExportRow[]): void {
  const sheetRows = rows.map(row => ({
    'Item Type':          row.itemType === 'WINE' ? 'wine' : 'generic',
    'Item Name':          row.itemName,
    'Size Value':         row.sizeValue ?? '',
    'Size Unit':          row.sizeUnit ?? '',
    'Active':             row.active ? 'true' : 'false',
    'Bin Location':       row.binLocation ?? '',
    'Par Level':          row.parLevel ?? '',
    'Reorder Point':      row.reorderPoint ?? '',
    'Location':           '',
    'Supplier Name':      row.supplierName ?? '',
    'Supplier Item Code': row.supplierItemCode ?? '',
    'Cost':               row.cost ?? '',
    'Wine List Name':     row.wineListName ?? '',
    'Selling Price':      row.sellingPrice ?? '',
    'Lot Tracked':        row.lotTracked ? 'TRUE' : 'FALSE',
  }));

  const ws = XLSX.utils.json_to_sheet(sheetRows, { header: INVENTORY_IMPORT_HEADERS });
  ws['!cols'] = INVENTORY_IMPORT_HEADERS.map(h =>
    ({ wch: Math.max(h.length + 2, 14) })
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Inventory Items');
  XLSX.writeFile(wb, 'inventory-items-export.xlsx');
}

// ── Import / export service ───────────────────────────────────────────────────

const importBase = (venueId: string) => `/admin/venue/${venueId}/inventory/import`;
const exportBase = (venueId: string) => `/admin/venue/${venueId}/inventory/export`;

const inventoryImportService = {
  preview: async (
    venueId: string,
    file: File,
  ): Promise<InventoryImportPreviewResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    // Content-Type must be absent so the browser sets multipart/form-data;boundary=…
    // The shared api instance defaults to application/json which breaks multer parsing.
    const res = await api.post(`${importBase(venueId)}/preview`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return res.data as InventoryImportPreviewResponse;
  },

  commit: async (
    venueId: string,
    file: File,
  ): Promise<InventoryImportCommitResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`${importBase(venueId)}/commit`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return res.data as InventoryImportCommitResponse;
  },

  exportItems: async (venueId: string): Promise<InventoryExportRow[]> => {
    const res = await api.get(exportBase(venueId));
    return res.data as InventoryExportRow[];
  },
};

export default inventoryImportService;
